import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, map, of, shareReplay, tap } from 'rxjs';
import { AuthService } from './auth';
import { SILENT_REQUEST } from './http-context';
import { SessionService } from './session';
import { ToastService } from './toast';
import { LiveQueryService } from '../other/Live-Query.service';

/**
 * Parse's code for a session token that is expired, revoked, or simply unknown
 * to the server. It comes back with an HTTP 400, not a 401, so the status alone
 * is not enough to recognise it.
 */
const INVALID_SESSION_TOKEN = 209;

/** True when the server rejected the request because the token is no longer good. */
export function isInvalidSessionToken(error: HttpErrorResponse): boolean {
  if (error.status === 401) {
    return true;
  }

  const body = error.error as { code?: number } | null;
  return typeof body === 'object' && body !== null && body.code === INVALID_SESSION_TOKEN;
}

/** What the app should do about a request the server rejected for a stale token. */
export type SessionRecovery = 'renew-guest' | 'sign-out';

@Injectable({ providedIn: 'root' })
export class SessionExpiryService {
  private readonly sessionService = inject(SessionService);
  private readonly authService = inject(AuthService);
  private readonly liveQuery = inject(LiveQueryService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);

  /** The in-flight guest re-login, shared by every request waiting on it. */
  private guestRenewal?: Observable<string | null>;

  /**
   * Guests never chose to log in, so bouncing them to the login screen when
   * their anonymous token ages out would be wrong — they get a new one behind
   * the scenes. A real account has nothing to refresh with (the backend issues
   * a single long-lived session token and no refresh token), so the only honest
   * move is to end the session and ask for a fresh sign-in.
   */
  recoveryFor(): SessionRecovery {
    return this.sessionService.isAuthenticated() ? 'sign-out' : 'renew-guest';
  }

  /**
   * Ends an expired session: drops the stored credentials, tells the user why,
   * and sends them to the login screen with the page they were on remembered.
   *
   * Idempotent — a burst of requests failing together only signs out once,
   * because the first call clears the account the check below depends on.
   */
  expireSession(): void {
    if (!this.sessionService.isAuthenticated()) {
      return;
    }

    const returnUrl = this.router.url;

    this.sessionService.clearSession();
    this.liveQuery.close();
    this.toastService.showToast(
      'warn',
      'Session expired',
      'Please log in again to continue.',
    );

    this.router.navigate(['/auth/login'], {
      queryParams: returnUrl.startsWith('/auth') ? {} : { returnUrl },
      replaceUrl: true,
    });

    // Public pages still talk to the backend, so put a fresh guest session in
    // place — the same thing an explicit logout does.
    this.renewGuestSession().subscribe();
  }

  /**
   * Swaps the stale anonymous token for a new one.
   *
   * The observable is cached while it runs so a page that fired ten requests at
   * once triggers one re-login rather than ten, and every waiting request picks
   * up the same token. Emits `null` if the re-login itself fails; callers then
   * surface the original error instead of retrying blind.
   */
  renewGuestSession(): Observable<string | null> {
    this.guestRenewal ??= this.authService
      .loginAnonymous(
        { id: this.authService.generateGuestId() },
        { context: new HttpContext().set(SILENT_REQUEST, true) },
      )
      .pipe(
        map((response) => response?.anonymousUser?.sessionToken ?? null),
        tap((token) => {
          if (token) {
            this.sessionService.saveToken(token);
          }
        }),
        catchError(() => of(null)),
        finalize(() => {
          this.guestRenewal = undefined;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    return this.guestRenewal;
  }
}
